import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Card,
  Dialog,
  H2,
  H4,
  Input,
  Paragraph,
  Label,
  SizableText,
  Spinner,
  XStack,
  YStack,
} from 'tamagui';
import { Link } from 'react-router-dom';
import { api, type Vehicle } from '../lib/api';
import { friendlyErrorMessage } from '../lib/errors';
import { unitSuffix, useUnitSystem } from '../lib/user-prefs';
import { LocationFields, type LocationValue } from '../components/LocationFields';
import { DeleteVehicleDialog } from '../components/DeleteVehicleDialog';
import { SkeletonPage } from '../components/Skeleton';

export function Vehicles() {
  const qc = useQueryClient();
  const vehiclesQuery = useQuery({ queryKey: ['vehicles'], queryFn: api.listVehicles });
  const [showAdd, setShowAdd] = useState(false);
  const [toDelete, setToDelete] = useState<Vehicle | null>(null);

  const deleteMut = useMutation({
    mutationFn: (id: string) => api.deleteVehicle(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['vehicles'] });
      setToDelete(null);
    },
  });

  if (vehiclesQuery.isLoading) {
    return <SkeletonPage headingWidth={160} />;
  }
  const vehicles = vehiclesQuery.data ?? [];

  return (
    <YStack gap="$4">
      <XStack alignItems="center" justifyContent="space-between">
        <H2>Vehicles</H2>
        <Button theme="blue" onPress={() => setShowAdd(true)}>
          Add vehicle
        </Button>
      </XStack>

      {vehicles.length === 0 ? (
        <Card padding="$4" elevate>
          <YStack gap="$2">
            <H4>Your garage is empty</H4>
            <Paragraph color="$color10">
              Add your first vehicle to start tracking maintenance, recalls, and service history.
            </Paragraph>
            <Button
              theme="blue"
              alignSelf="flex-start"
              marginTop="$2"
              onPress={() => setShowAdd(true)}
            >
              Add your first vehicle
            </Button>
          </YStack>
        </Card>
      ) : (
        <YStack gap="$2">
          {vehicles.map((v) => (
            <VehicleRow key={v.id} vehicle={v} onDelete={() => setToDelete(v)} />
          ))}
        </YStack>
      )}

      <AddVehicleDialog
        open={showAdd}
        onClose={() => setShowAdd(false)}
        onSuccess={() => {
          setShowAdd(false);
          qc.invalidateQueries({ queryKey: ['vehicles'] });
        }}
      />

      <DeleteVehicleDialog
        vehicle={toDelete}
        onCancel={() => setToDelete(null)}
        onConfirm={() => toDelete && deleteMut.mutate(toDelete.id)}
        isPending={deleteMut.isPending}
      />
    </YStack>
  );
}

function VehicleRow({ vehicle, onDelete }: { vehicle: Vehicle; onDelete: () => void }) {
  const suffix = unitSuffix(useUnitSystem());
  return (
    <Card padding="$3" elevate>
      <XStack alignItems="center" justifyContent="space-between" gap="$2" flexWrap="wrap">
        <YStack flex={1} minWidth={180}>
          <H4>
            {vehicle.year} {vehicle.make} {vehicle.model}
          </H4>
          <Paragraph color="$color10" size="$2">
            {vehicle.currentMileage.toLocaleString()} {suffix} · {vehicle.vin ?? 'no VIN'}
          </Paragraph>
        </YStack>
        <XStack gap="$2" alignItems="center">
          <Link to={`/vehicles/${vehicle.id}`} style={LINK_RESET}>
            <SizableText color="$accent" size="$3" minHeight={44} lineHeight={44}>
              Open
            </SizableText>
          </Link>
          <Button size="$3" theme="red" onPress={onDelete}>
            Delete
          </Button>
        </XStack>
      </XStack>
    </Card>
  );
}

// Link reset so the Tamagui child owns color/decoration (theme-aware accent).
const LINK_RESET: React.CSSProperties = { textDecoration: 'none', color: 'inherit' };

// 17 chars, alphanumeric, excluding I/O/Q (standard VIN charset). Mirrors the
// server-side @cartrack/shared validation so we can gate "Look up" client-side.
const VIN_PATTERN = /^[A-HJ-NPR-Z0-9]{17}$/;

function AddVehicleDialog({
  open,
  onClose,
  onSuccess,
}: {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [mode, setMode] = useState<'vin' | 'manual'>('vin');
  const [vin, setVin] = useState('');
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [year, setYear] = useState('');
  const [trim, setTrim] = useState('');
  const [engine, setEngine] = useState('');
  const [mileage, setMileage] = useState('');
  const [garage, setGarage] = useState<LocationValue>({
    country: 'US',
    stateProvince: null,
    postalCode: null,
  });
  const [error, setError] = useState<string | null>(null);
  // Set once a VIN lookup has populated the fields; lets us show the editable
  // make/model/year cells (and confirm) instead of just the VIN input.
  const [decoded, setDecoded] = useState(false);

  const isVinComplete = VIN_PATTERN.test(vin);

  const lookupMut = useMutation({
    mutationFn: (v: string) => api.decodeVin(v),
    onSuccess: (result) => {
      setError(null);
      setMake(result.make ?? '');
      setModel(result.model ?? '');
      setYear(result.year != null ? String(result.year) : '');
      setTrim(result.trim ?? '');
      setEngine(result.engine ?? '');
      setDecoded(true);
    },
    onError: (err) => {
      setError(friendlyErrorMessage(err));
    },
  });

  const mut = useMutation({
    mutationFn: (dto: Parameters<typeof api.createVehicle>[0]) => api.createVehicle(dto),
    onSuccess: () => {
      setError(null);
      resetForm();
      onSuccess();
    },
    onError: (err) => {
      setError(friendlyErrorMessage(err));
    },
  });

  function resetForm(): void {
    setVin('');
    setMake('');
    setModel('');
    setYear('');
    setTrim('');
    setEngine('');
    setMileage('');
    setGarage({ country: 'US', stateProvince: null, postalCode: null });
    setDecoded(false);
    lookupMut.reset();
  }

  function lookup(): void {
    if (!isVinComplete) {
      setError('Enter a valid 17-character VIN (no I, O, or Q).');
      return;
    }
    setError(null);
    lookupMut.mutate(vin);
  }

  function submit(): void {
    const currentMileage = mileage ? Number(mileage) : undefined;
    const location = {
      garageCountry: garage.country,
      garageStateProvince: garage.stateProvince ?? undefined,
      garagePostalCode: garage.postalCode ?? undefined,
    };
    // Both paths send the reviewed make/model/year so the user's edits win. In
    // VIN mode we also persist the VIN itself; the server keeps these fields as
    // long as they're present (it only re-decodes when make/model/year are absent).
    const base = {
      make,
      model,
      year: Number(year),
      trim: trim || undefined,
      engine: engine || undefined,
      currentMileage,
      ...location,
    };
    if (mode === 'vin') {
      mut.mutate({ vin, ...base } as Parameters<typeof api.createVehicle>[0]);
    } else {
      mut.mutate(base as Parameters<typeof api.createVehicle>[0]);
    }
  }

  return (
    <Dialog modal open={open} onOpenChange={(o) => (o ? undefined : onClose())}>
      <Dialog.Portal>
        <Dialog.Overlay key="overlay" opacity={0.5} />
        <Dialog.Content
          key="content"
          elevate
          padding="$4"
          width={420}
          maxWidth="90vw"
          maxHeight="85vh"
          overflow="scroll"
          gap="$3"
        >
          <Dialog.Title>Add vehicle</Dialog.Title>
          <XStack gap="$2">
            <Button
              size="$2"
              theme={mode === 'vin' ? 'blue' : undefined}
              onPress={() => {
                setMode('vin');
                setError(null);
              }}
            >
              By VIN
            </Button>
            <Button
              size="$2"
              theme={mode === 'manual' ? 'blue' : undefined}
              onPress={() => {
                setMode('manual');
                setError(null);
              }}
            >
              Manual
            </Button>
          </XStack>

          {mode === 'vin' ? (
            <YStack gap="$2">
              <Label>VIN</Label>
              <XStack gap="$2" alignItems="flex-end">
                <Input
                  flex={1}
                  placeholder="17-character VIN"
                  value={vin}
                  onChangeText={(t) => {
                    setVin(t.toUpperCase());
                    if (decoded) setDecoded(false);
                  }}
                  maxLength={17}
                />
                <Button
                  theme="blue"
                  onPress={lookup}
                  disabled={!isVinComplete || lookupMut.isPending}
                >
                  {lookupMut.isPending ? <Spinner /> : 'Look up'}
                </Button>
              </XStack>
              {decoded ? (
                <Paragraph color="$color10" size="$2">
                  Review the details below and edit if anything looks off, then confirm.
                </Paragraph>
              ) : null}
            </YStack>
          ) : null}

          {mode === 'manual' || decoded ? (
            <YStack gap="$2">
              <Label>Make</Label>
              <Input value={make} onChangeText={setMake} placeholder="Toyota" />
              <Label>Model</Label>
              <Input value={model} onChangeText={setModel} placeholder="Camry" />
              <Label>Year</Label>
              <Input
                value={year}
                onChangeText={setYear}
                placeholder="2019"
                keyboardType="number-pad"
              />
              <Label>Trim (optional)</Label>
              <Input value={trim} onChangeText={setTrim} placeholder="LX" />
              <Label>Engine (optional)</Label>
              <Input value={engine} onChangeText={setEngine} placeholder="1.5L I4" />
            </YStack>
          ) : null}

          <Label>Current odometer (optional)</Label>
          <Input value={mileage} onChangeText={setMileage} keyboardType="number-pad" />

          <LocationFields value={garage} onChange={setGarage} labelPrefix="Garage" />

          {error ? <Paragraph color="$red10">{error}</Paragraph> : null}

          <XStack gap="$2" justifyContent="flex-end">
            <Button onPress={onClose} disabled={mut.isPending}>
              Cancel
            </Button>
            <Button
              theme="blue"
              onPress={submit}
              disabled={mut.isPending || (mode === 'vin' && !decoded)}
            >
              {mut.isPending ? 'Adding…' : 'Add'}
            </Button>
          </XStack>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog>
  );
}
